import { noteDisplayFields } from "@/lib/noteFields";

/**
 * LumeroFlow Shared PDF System
 * Consistent document generation across the entire SaaS.
 */

const fmtDate = (iso) => {
  if (!iso) return "";
  const [date, time] = iso.split("T");
  const [y, m, d] = date.split("-");
  const formatted = `${d}/${m}/${y}`;
  if (time) return `${formatted}, ${time.slice(0, 5)}`;
  return formatted;
};

const fmtDateOnly = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
};

const PDF_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1A1B1A; font-size: 13px; line-height: 1.55; }
  .pdf-page { padding: 28px 32px; max-width: 780px; }
  .pdf-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 2px solid #D6E2DD; }
  .pdf-header-left h1 { font-size: 18px; font-weight: 700; color: #1A1B1A; margin-bottom: 2px; }
  .pdf-header-left .subtitle { font-size: 12px; color: #496275; font-weight: 600; }
  .pdf-header-right { text-align: right; }
  .pdf-meta { font-size: 10px; color: #757675; line-height: 1.7; }
  .pdf-meta strong { color: #4A4B4A; font-weight: 600; }
  .pdf-badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
  .pdf-badge-final { background: #E4F0EF; color: #205B5A; }
  .pdf-badge-draft { background: #FEF3CD; color: #8B6914; }
  .pdf-section { margin-bottom: 14px; }
  .pdf-section-heading { font-size: 11px; font-weight: 700; color: #2A3A35; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
  .pdf-section-content { font-size: 13px; color: #4A4B4A; line-height: 1.6; white-space: pre-wrap; }
  .pdf-body-charts { margin-top: 16px; padding-top: 12px; border-top: 1px solid #E9F0ED; }
  .pdf-body-charts h3 { font-size: 11px; font-weight: 700; color: #2A3A35; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
  .pdf-chart-row { display: flex; gap: 16px; flex-wrap: wrap; }
  .pdf-chart-item { width: 420px; border: 1px solid #D6E2DD; border-radius: 8px; overflow: hidden; background: #FAFBFA; position: relative; }
  .pdf-chart-item img { width: 100%; height: auto; display: block; }
  .pdf-chart-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
  .pdf-chart-label { font-size: 9px; color: #757675; text-align: center; padding: 4px; background: white; }
  .pdf-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #E9F0ED; display: flex; justify-content: space-between; font-size: 9px; color: #757675; }
  .pdf-clinic-brand { font-size: 10px; color: #205B5A; font-weight: 600; margin-bottom: 4px; }
  .pdf-note-divider { page-break-before: always; margin-top: 30px; padding-top: 20px; border-top: 3px solid #D6E2DD; }
  .pdf-note-divider:first-child { page-break-before: auto; margin-top: 0; padding-top: 0; border-top: none; }
  .pdf-note-header { background: #F8FAF9; border: 1px solid #E9F0ED; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
  .pdf-note-title { font-size: 15px; font-weight: 700; color: #1A1B1A; margin-bottom: 2px; }
  .pdf-note-meta { font-size: 10px; color: #757675; line-height: 1.6; }
`;


// Build SVG overlay for body chart annotations — WYSIWYG match, including arrows
function buildChartSvg(chart) {
  if (!chart || (!chart.drawings?.length && !chart.texts?.length)) return "";
  const drawings = (chart.drawings || []).map(d => {
    if (d.type === "arrow" && d.points?.length >= 2) {
      const [p1, p2] = d.points;
      // Arrow with inline arrowhead
      const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      const headLen = 12;
      const x3 = p2.x - headLen * Math.cos(angle - Math.PI / 6);
      const y3 = p2.y - headLen * Math.sin(angle - Math.PI / 6);
      const x4 = p2.x - headLen * Math.cos(angle + Math.PI / 6);
      const y4 = p2.y - headLen * Math.sin(angle + Math.PI / 6);
      return `<line x1="${p1.x}" y1="${p1.y}" x2="${p2.x}" y2="${p2.y}" stroke="${d.color}" stroke-width="${d.width + 1}" /><polygon points="${p2.x},${p2.y} ${x3},${y3} ${x4},${y4}" fill="${d.color}" />`;
    }
    return `<polyline points="${d.points?.map(p => `${p.x},${p.y}`).join(" ")}" fill="none" stroke="${d.color}" stroke-width="${d.width}" stroke-linecap="round" stroke-linejoin="round" />`;
  }).join("");
  const texts = (chart.texts || []).map(t =>
    `<text x="${t.x}" y="${t.y}" fill="${t.color}" font-size="12" font-weight="500" font-family="sans-serif">${t.text}</text>`
  ).join("");
  return `<svg class="pdf-chart-svg" viewBox="0 0 600 700" preserveAspectRatio="xMidYMid meet">${drawings}${texts}</svg>`;
}

function buildNoteSections(note) {
  const fields = noteDisplayFields(note);
  return fields.map(f =>
    `<div class="pdf-section"><div class="pdf-section-heading">${f.label}</div><div class="pdf-section-content">${f.value}</div></div>`
  ).join("");
}


// Convert an image URL to a fully-loaded base64 data URI
// Returns both the data URI and confirms the image is decoded/renderable
async function loadImageAsDataUri(url) {
  try {
    // For local URLs (same-origin), we can fetch without CORS issues
    // For external URLs, fetch may fail due to CORS
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const dataUri = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
    
    // Pre-load the image to ensure it's fully decoded
    // This is critical - html2canvas captures blank images if not pre-loaded
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image decode failed'));
      img.src = dataUri;
    });
    
    return dataUri;
  } catch (e) {
    console.warn('loadImageAsDataUri failed, trying direct image load:', url, e);
    // Fallback: try loading the image directly without fetch
    // This works for same-origin images that React's dev server proxies
    try {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Draw to canvas to extract base64
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          try {
            const dataUri = canvas.toDataURL('image/png');
            resolve(dataUri);
          } catch (canvasErr) {
            // Canvas tainted, fall back to original URL
            resolve(url);
          }
        };
        img.onerror = () => resolve(url);
        img.src = url;
      });
    } catch {
      return url;
    }
  }
}

async function buildBodyChartsHtml(charts, getImageUrl) {
  if (!charts || charts.length === 0) return "";
  
  // Pre-load ALL images first (ensures they're decoded before DOM insertion)
  const imagePromises = charts.map(async (bc) => {
    const rawUrl = getImageUrl ? getImageUrl(bc.templateId) : "";
    return rawUrl ? await loadImageAsDataUri(rawUrl) : "";
  });
  const loadedImages = await Promise.all(imagePromises);
  
  const items = charts.map((bc, i) => {
    const imgSrc = loadedImages[i];
    const svg = buildChartSvg(bc);
    const label = bc.templateId?.replace(/-/g, " ") || `Chart ${i + 1}`;
    // Use inline styles for dimensions to ensure html2canvas captures correctly
    return `<div class="pdf-chart-item">
      <div style="position:relative; width:420px; min-height:490px; background:#FAFBFA;">
        <img src="${imgSrc}" alt="${label}" style="width:100%; height:auto; display:block;" />
        ${svg}
      </div>
      <div class="pdf-chart-label">${label}</div>
    </div>`;
  });
  
  return `<div class="pdf-body-charts"><h3>Body charts</h3><div class="pdf-chart-row">${items.join("")}</div></div>`;
}

// Wait for all images in a DOM element to be fully loaded/decoded
async function waitForImagesToLoad(element) {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map(img => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve; // Don't block on failed images
      // Force re-trigger load if src is already set
      if (img.src) {
        const src = img.src;
        img.src = '';
        img.src = src;
      }
    });
  });
  await Promise.all(promises);
  // Extra frame delay to ensure browser has painted
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
}

/**
 * Generate a single treatment note PDF
 */
export async function generateNotePdf(note, { patient, clinic, bodyChartImages, getTemplateImageFn } = {}) {
  const html2pdf = (await import("html2pdf.js")).default;
  const patientName = patient ? `${patient.title ? patient.title + " " : ""}${patient.first_name} ${patient.last_name}` : "";
  const clinicName = clinic?.name || "";
  const status = note.status === "final" ? "Final" : "Draft";
  const badgeClass = note.status === "final" ? "pdf-badge-final" : "pdf-badge-draft";
  const sectionsHtml = buildNoteSections(note);
  const bodyChartsHtml = await buildBodyChartsHtml(note.body_charts, getTemplateImageFn);

  const html = `<html><head><style>${PDF_STYLES}</style></head><body><div class="pdf-page">
    <div class="pdf-header">
      <div class="pdf-header-left">
        ${clinicName ? `<div class="pdf-clinic-brand">${clinicName}</div>` : ""}
        <h1>${patientName || "Treatment note"}</h1>
        <div class="subtitle">${note.appointment_type || "Treatment note"} <span class="pdf-badge ${badgeClass}">${status}</span></div>
      </div>
      <div class="pdf-header-right">
        <div class="pdf-meta">
          ${note.practitioner_name ? `<div><strong>Practitioner:</strong> ${note.practitioner_name}</div>` : ""}
          ${note.created_at ? `<div><strong>Created:</strong> ${fmtDate(note.created_at)}</div>` : ""}
          ${note.updated_at ? `<div><strong>Last updated:</strong> ${fmtDate(note.updated_at)}</div>` : ""}
        </div>
      </div>
    </div>
    ${sectionsHtml}
    ${bodyChartsHtml}
    <div class="pdf-footer">
      <div>${patientName}${clinicName ? ` | ${clinicName}` : ""}${note.updated_at ? ` | Last updated ${fmtDate(note.updated_at)}` : ""}</div>
      <div>Page 1 of 1</div>
    </div>
  </div></body></html>`;

  const el = document.createElement("div");
  el.innerHTML = html;
  document.body.appendChild(el);
  
  // Critical: Wait for all images to be fully loaded before html2canvas captures
  await waitForImagesToLoad(el);
  
  await html2pdf().set({ margin: [12, 10, 12, 10], filename: `${note.appointment_type || "note"}-${fmtDateOnly(note.created_at)}.pdf`, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2, useCORS: true, logging: false, allowTaint: true }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } }).from(el).save();
  document.body.removeChild(el);
}

/**
 * Generate a full treatment history PDF for a patient (all notes combined)
 */
export async function generateFullHistoryPdf(notes, { patient, clinic, getTemplateImageFn } = {}) {
  const html2pdf = (await import("html2pdf.js")).default;
  const patientName = patient ? `${patient.title ? patient.title + " " : ""}${patient.first_name} ${patient.last_name}` : "";
  const clinicName = clinic?.name || "";

  // Sort chronologically: oldest first
  const sorted = [...notes].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  const notesSections = [];
  for (const note of sorted) {
    const i = notesSections.length;
    const status = note.status === "final" ? "Final" : "Draft";
    const badgeClass = note.status === "final" ? "pdf-badge-final" : "pdf-badge-draft";
    const sectionsHtml = buildNoteSections(note);
    const bodyChartsHtml = await buildBodyChartsHtml(note.body_charts, getTemplateImageFn);
    notesSections.push(`<div class="${i > 0 ? "pdf-note-divider" : ""}">
      <div class="pdf-note-header">
        <div class="pdf-note-title">${note.appointment_type || "Treatment note"} <span class="pdf-badge ${badgeClass}">${status}</span></div>
        <div class="pdf-note-meta">
          ${note.practitioner_name ? `<span><strong>Practitioner:</strong> ${note.practitioner_name}</span> · ` : ""}
          ${note.created_at ? `<span><strong>Date:</strong> ${fmtDate(note.created_at)}</span>` : ""}
          ${note.updated_at && note.updated_at !== note.created_at ? ` · <span><strong>Updated:</strong> ${fmtDate(note.updated_at)}</span>` : ""}
        </div>
      </div>
      ${sectionsHtml}
      ${bodyChartsHtml}
    </div>`);
  }
  const notesHtml = notesSections.join("");

  const html = `<html><head><style>${PDF_STYLES}</style></head><body><div class="pdf-page">
    <div class="pdf-header">
      <div class="pdf-header-left">
        ${clinicName ? `<div class="pdf-clinic-brand">${clinicName}</div>` : ""}
        <h1>${patientName} — Treatment history</h1>
        <div class="subtitle">${sorted.length} treatment notes</div>
      </div>
      <div class="pdf-header-right">
        <div class="pdf-meta"><div><strong>Exported:</strong> ${fmtDate(new Date().toISOString())}</div></div>
      </div>
    </div>
    ${notesHtml}
    <div class="pdf-footer">
      <div>${patientName}${clinicName ? ` | ${clinicName}` : ""} | Full treatment history</div>
      <div></div>
    </div>
  </div></body></html>`;

  const el = document.createElement("div");
  el.innerHTML = html;
  document.body.appendChild(el);
  
  // Critical: Wait for all images to be fully loaded before html2canvas captures
  await waitForImagesToLoad(el);
  
  await html2pdf().set({ margin: [12, 10, 12, 10], filename: `${patientName.replace(/\s+/g, "-")}-treatment-history.pdf`, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2, useCORS: true, logging: false, allowTaint: true }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } }).from(el).save();
  document.body.removeChild(el);
}

/**
 * Generate an invoice PDF
 */
export async function generateInvoicePdf(invoice) {
  const html2pdf = (await import("html2pdf.js")).default;
  const el = document.querySelector('[data-testid="invoice-document"]');
  if (!el) return;
  await html2pdf().set({ margin: [10, 10, 10, 10], filename: `${invoice?.invoice_number || "invoice"}.pdf`, image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, logging: false }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } }).from(el).save();
}
